/**
 * Expanded Career Database
 * 50+ no-degree-required careers with complete data
 * Organized by category for easy navigation
 */

import { CareerPath } from '../careerPaths';

export const EXPANDED_CAREERS: CareerPath[] = [
  // ===================================
  // CUSTOMER SERVICE & SUPPORT (8 careers)
  // ===================================
  {
    id: 'customer_success',
    title: 'Customer Success Specialist',
    category: 'Customer Service',
    keywords: ['customer_service', 'communication', 'problem_solving', 'tech', 'people', 'support'],
    salary: { entry_min: 40000, entry_max: 55000 },
    description: 'Help customers succeed with products/services through proactive outreach, troubleshooting, and relationship building.',
    dailyActivities: [
      'Respond to customer emails, chats, and calls',
      'Troubleshoot product issues',
      'Track customer satisfaction metrics',
      'Conduct onboarding calls for new customers'
    ],
    requiredSkills: ['customer_service', 'communication', 'problem_solving', 'basic tech literacy'],
    niceToHave: ['CRM experience', 'Salesforce', 'data entry'],
    commonTransitionsFrom: ['retail', 'restaurant', 'call center', 'hospitality']
  },
  {
    id: 'technical_support',
    title: 'Technical Support Specialist',
    category: 'Customer Service',
    keywords: ['technical_support', 'IT', 'troubleshooting', 'problem_solving', 'customer_service'],
    salary: { entry_min: 38000, entry_max: 52000 },
    description: 'Provide technical assistance to customers experiencing hardware, software, or connectivity issues.',
    dailyActivities: [
      'Diagnose technical problems over phone/chat',
      'Guide customers through step-by-step solutions',
      'Document common issues and resolutions',
      'Escalate complex problems to senior technicians'
    ],
    requiredSkills: ['technical troubleshooting', 'patience', 'communication', 'logical thinking'],
    niceToHave: ['CompTIA A+', 'specific software knowledge', 'remote desktop tools'],
    commonTransitionsFrom: ['retail tech sales', 'computer repair', 'IT help desk', 'any customer service']
  },
  {
    id: 'call_center_rep',
    title: 'Call Center Representative',
    category: 'Customer Service',
    keywords: ['customer_service', 'communication', 'sales', 'problem_solving', 'people'],
    salary: { entry_min: 30000, entry_max: 42000 },
    description: 'Handle inbound/outbound calls to assist customers with inquiries, sales, or technical support.',
    dailyActivities: [
      'Answer high volume of calls professionally',
      'Process orders and handle complaints',
      'Meet call quality and productivity metrics',
      'Update customer records in CRM'
    ],
    requiredSkills: ['communication', 'typing', 'active listening', 'stress management'],
    niceToHave: ['sales experience', 'bilingual', 'conflict resolution'],
    commonTransitionsFrom: ['retail', 'food service', 'receptionist', 'any entry-level']
  },
  {
    id: 'account_coordinator',
    title: 'Account Coordinator',
    category: 'Customer Service',
    keywords: ['coordination', 'customer_service', 'organization', 'admin', 'communication'],
    salary: { entry_min: 38000, entry_max: 50000 },
    description: 'Coordinate client accounts, ensuring smooth communication between clients and internal teams.',
    dailyActivities: [
      'Manage client communication and requests',
      'Schedule meetings and coordinate deliverables',
      'Track project timelines and budgets',
      'Prepare reports and presentations'
    ],
    requiredSkills: ['organization', 'communication', 'multitasking', 'attention to detail'],
    niceToHave: ['project management', 'Microsoft Office', 'industry knowledge'],
    commonTransitionsFrom: ['admin assistant', 'customer service', 'sales support', 'receptionist']
  },
  {
    id: 'client_services_coordinator',
    title: 'Client Services Coordinator',
    category: 'Customer Service',
    keywords: ['customer_service', 'coordination', 'admin', 'organization', 'communication'],
    salary: { entry_min: 36000, entry_max: 48000 },
    description: 'Support client-facing teams by coordinating services, scheduling, and maintaining client satisfaction.',
    dailyActivities: [
      'Process client requests and onboarding',
      'Coordinate service delivery with operations team',
      'Maintain client records and documentation',
      'Handle billing inquiries and invoice questions'
    ],
    requiredSkills: ['organization', 'customer service', 'administrative', 'communication'],
    niceToHave: ['scheduling software', 'billing systems', 'data entry speed'],
    commonTransitionsFrom: ['receptionist', 'admin', 'hospitality', 'retail management']
  },
  {
    id: 'customer_service_trainer',
    title: 'Customer Service Trainer',
    category: 'Customer Service',
    keywords: ['training', 'customer_service', 'mentoring', 'communication', 'education'],
    salary: { entry_min: 42000, entry_max: 56000 },
    description: 'Train new customer service representatives on company policies, systems, and best practices.',
    dailyActivities: [
      'Develop training materials and guides',
      'Conduct onboarding sessions for new hires',
      'Observe and coach existing team members',
      'Create assessment tools and track performance'
    ],
    requiredSkills: ['customer service experience', 'presentation', 'patience', 'mentoring'],
    niceToHave: ['training certification', 'curriculum development', 'LMS platforms'],
    commonTransitionsFrom: ['senior customer service', 'team lead', 'call center supervisor']
  },
  {
    id: 'retention_specialist',
    title: 'Customer Retention Specialist',
    category: 'Customer Service',
    keywords: ['customer_service', 'sales', 'problem_solving', 'communication', 'negotiation'],
    salary: { entry_min: 40000, entry_max: 58000 },
    description: 'Work to retain customers who are considering leaving by addressing concerns and offering solutions.',
    dailyActivities: [
      'Contact at-risk customers proactively',
      'Identify reasons for dissatisfaction',
      'Offer retention incentives and solutions',
      'Track retention metrics and success rates'
    ],
    requiredSkills: ['customer service', 'problem-solving', 'negotiation', 'empathy'],
    niceToHave: ['sales experience', 'conflict resolution', 'analytics'],
    commonTransitionsFrom: ['customer service', 'sales', 'account management']
  },
  {
    id: 'community_moderator',
    title: 'Community Moderator',
    category: 'Customer Service',
    keywords: ['customer_service', 'communication', 'social_media', 'conflict', 'moderation'],
    salary: { entry_min: 35000, entry_max: 48000 },
    description: 'Monitor and moderate online communities, forums, and social media to maintain positive environment.',
    dailyActivities: [
      'Review and moderate user-generated content',
      'Enforce community guidelines consistently',
      'Respond to user questions and concerns',
      'Report trends and escalate serious issues'
    ],
    requiredSkills: ['communication', 'judgment', 'social media', 'conflict resolution'],
    niceToHave: ['specific platform experience', 'multilingual', 'content moderation tools'],
    commonTransitionsFrom: ['social media', 'customer service', 'retail', 'gaming']
  },

  // ===================================
  // HEALTHCARE & SOCIAL SERVICES (7 careers)
  // ===================================
  {
    id: 'healthcare_support',
    title: 'Healthcare Support Specialist',
    category: 'Healthcare',
    keywords: ['care', 'healthcare', 'people', 'support', 'medical', 'patient'],
    salary: { entry_min: 35000, entry_max: 48000 },
    description: 'Support healthcare facilities with patient coordination, administrative tasks, and ensuring smooth operations.',
    dailyActivities: [
      'Schedule patient appointments',
      'Maintain patient records',
      'Coordinate between patients and medical staff',
      'Handle insurance verification'
    ],
    requiredSkills: ['communication', 'empathy', 'organization', 'confidentiality'],
    niceToHave: ['medical terminology', 'HIPAA knowledge', 'EMR systems'],
    commonTransitionsFrom: ['caregiver', 'nanny', 'customer service', 'admin assistant']
  },
  {
    id: 'medical_receptionist',
    title: 'Medical Receptionist',
    category: 'Healthcare',
    keywords: ['healthcare', 'admin', 'customer_service', 'organization', 'medical'],
    salary: { entry_min: 30000, entry_max: 40000 },
    description: 'Greet patients, schedule appointments, handle billing, and maintain medical office operations.',
    dailyActivities: [
      'Check in patients and verify insurance',
      'Answer phones and schedule appointments',
      'Process payments and billing',
      'Maintain patient files and confidentiality'
    ],
    requiredSkills: ['customer service', 'organization', 'multitasking', 'basic computer skills'],
    niceToHave: ['medical billing', 'EMR systems', 'medical terminology'],
    commonTransitionsFrom: ['receptionist', 'customer service', 'retail', 'admin']
  },
  {
    id: 'patient_care_coordinator',
    title: 'Patient Care Coordinator',
    category: 'Healthcare',
    keywords: ['healthcare', 'coordination', 'patient', 'organization', 'care', 'admin'],
    salary: { entry_min: 38000, entry_max: 50000 },
    description: 'Coordinate patient care across providers, schedule treatments, and ensure continuity of care.',
    dailyActivities: [
      'Schedule procedures and specialist appointments',
      'Coordinate with insurance companies',
      'Follow up on patient treatment plans',
      'Maintain accurate medical records'
    ],
    requiredSkills: ['organization', 'healthcare knowledge', 'communication', 'empathy'],
    niceToHave: ['medical terminology', 'care management software', 'HIPAA certification'],
    commonTransitionsFrom: ['medical receptionist', 'caregiver', 'healthcare support', 'admin']
  },
  {
    id: 'home_health_aide',
    title: 'Home Health Aide',
    category: 'Healthcare',
    keywords: ['care', 'healthcare', 'people', 'support', 'caregiving', 'hands-on'],
    salary: { entry_min: 28000, entry_max: 38000 },
    description: 'Provide in-home care to elderly, disabled, or recovering patients with daily living activities.',
    dailyActivities: [
      'Assist with bathing, dressing, and grooming',
      'Prepare meals and help with eating',
      'Monitor vital signs and medication',
      'Provide companionship and emotional support'
    ],
    requiredSkills: ['caregiving', 'patience', 'physical stamina', 'empathy'],
    niceToHave: ['CNA certification', 'CPR/First Aid', 'specific condition experience'],
    commonTransitionsFrom: ['caregiver', 'childcare', 'hospitality', 'any entry-level']
  },
  {
    id: 'medical_billing_specialist',
    title: 'Medical Billing Specialist',
    category: 'Healthcare',
    keywords: ['healthcare', 'admin', 'finance', 'detail', 'medical', 'billing'],
    salary: { entry_min: 36000, entry_max: 48000 },
    description: 'Process medical insurance claims, verify patient benefits, and handle healthcare billing operations.',
    dailyActivities: [
      'Submit insurance claims and track payments',
      'Verify patient insurance eligibility',
      'Code procedures using ICD-10 and CPT',
      'Resolve billing discrepancies and denials'
    ],
    requiredSkills: ['attention to detail', 'medical terminology', 'data entry', 'problem-solving'],
    niceToHave: ['medical billing certification', 'coding knowledge', 'specific billing software'],
    commonTransitionsFrom: ['medical receptionist', 'data entry', 'admin', 'insurance']
  },
  {
    id: 'dental_assistant',
    title: 'Dental Assistant',
    category: 'Healthcare',
    keywords: ['healthcare', 'hands-on', 'dental', 'care', 'technical', 'patient'],
    salary: { entry_min: 35000, entry_max: 46000 },
    description: 'Assist dentists during procedures, prepare treatment rooms, and manage patient records.',
    dailyActivities: [
      'Prepare patients and treatment rooms',
      'Assist dentist during procedures',
      'Take X-rays and impressions',
      'Sterilize instruments and maintain equipment'
    ],
    requiredSkills: ['hands-on technical', 'attention to detail', 'patient care', 'sterilization'],
    niceToHave: ['dental assistant certification', 'X-ray certification', 'dental software'],
    commonTransitionsFrom: ['caregiver', 'medical assistant', 'healthcare support', 'entry-level']
  },
  {
    id: 'pharmacy_technician',
    title: 'Pharmacy Technician',
    category: 'Healthcare',
    keywords: ['healthcare', 'technical', 'detail', 'pharmacy', 'customer_service', 'medical'],
    salary: { entry_min: 32000, entry_max: 42000 },
    description: 'Assist pharmacists in filling prescriptions, managing inventory, and serving patients.',
    dailyActivities: [
      'Fill and label prescriptions accurately',
      'Manage medication inventory and ordering',
      'Process insurance claims for medications',
      'Answer patient questions about prescriptions'
    ],
    requiredSkills: ['attention to detail', 'customer service', 'math', 'computer skills'],
    niceToHave: ['pharmacy tech certification', 'state license', 'retail experience'],
    commonTransitionsFrom: ['retail', 'customer service', 'medical receptionist', 'entry-level']
  },

  // ===================================
  // OPERATIONS & LOGISTICS (8 careers)
  // ===================================
  {
    id: 'logistics_coordinator',
    title: 'Logistics Coordinator',
    category: 'Operations',
    keywords: ['operations', 'logistics', 'organized', 'warehouse', 'shipping', 'inventory'],
    salary: { entry_min: 38000, entry_max: 52000 },
    description: 'Coordinate the movement of goods, manage inventory, and ensure timely delivery of products.',
    dailyActivities: [
      'Track shipments and inventory levels',
      'Coordinate with warehouses and vendors',
      'Resolve shipping delays and issues',
      'Maintain accurate records in tracking systems'
    ],
    requiredSkills: ['organization', 'attention to detail', 'time management', 'basic computer skills'],
    niceToHave: ['forklift certification', 'supply chain knowledge', 'Excel proficiency'],
    commonTransitionsFrom: ['warehouse', 'delivery driver', 'retail', 'manufacturing']
  },
  {
    id: 'warehouse_supervisor',
    title: 'Warehouse Supervisor',
    category: 'Operations',
    keywords: ['operations', 'warehouse', 'management', 'logistics', 'organized', 'leadership'],
    salary: { entry_min: 42000, entry_max: 58000 },
    description: 'Supervise warehouse staff, manage inventory, and ensure efficient receiving/shipping operations.',
    dailyActivities: [
      'Oversee warehouse team and assign tasks',
      'Manage inventory accuracy and organization',
      'Ensure safety compliance and training',
      'Coordinate with logistics on shipping schedules'
    ],
    requiredSkills: ['leadership', 'organization', 'warehouse operations', 'safety protocols'],
    niceToHave: ['forklift certification', 'WMS software', 'previous warehouse experience'],
    commonTransitionsFrom: ['warehouse worker', 'logistics', 'retail management', 'manufacturing']
  },
  {
    id: 'inventory_specialist',
    title: 'Inventory Specialist',
    category: 'Operations',
    keywords: ['operations', 'inventory', 'detail', 'organized', 'warehouse', 'data'],
    salary: { entry_min: 35000, entry_max: 46000 },
    description: 'Manage inventory levels, conduct physical counts, and maintain accurate stock records.',
    dailyActivities: [
      'Perform cycle counts and physical inventories',
      'Track inventory movements and discrepancies',
      'Update inventory management systems',
      'Coordinate with purchasing on stock levels'
    ],
    requiredSkills: ['attention to detail', 'organization', 'data entry', 'analytical thinking'],
    niceToHave: ['inventory management software', 'Excel', 'supply chain knowledge'],
    commonTransitionsFrom: ['warehouse', 'retail', 'admin', 'data entry']
  },
  {
    id: 'operations_coordinator',
    title: 'Operations Coordinator',
    category: 'Operations',
    keywords: ['operations', 'coordination', 'organization', 'project_management', 'admin'],
    salary: { entry_min: 40000, entry_max: 54000 },
    description: 'Support daily operations by coordinating between departments, tracking projects, and solving operational issues.',
    dailyActivities: [
      'Coordinate cross-functional projects',
      'Track operational metrics and KPIs',
      'Solve day-to-day operational problems',
      'Prepare reports and documentation'
    ],
    requiredSkills: ['organization', 'multitasking', 'communication', 'problem-solving'],
    niceToHave: ['project management', 'process improvement', 'specific industry knowledge'],
    commonTransitionsFrom: ['admin assistant', 'customer service', 'logistics', 'retail management']
  },
  {
    id: 'quality_control_inspector',
    title: 'Quality Control Inspector',
    category: 'Operations',
    keywords: ['qa', 'detail', 'analysis', 'manufacturing', 'technical', 'inspection'],
    salary: { entry_min: 36000, entry_max: 50000 },
    description: 'Inspect products for quality standards, identify defects, and ensure compliance with specifications.',
    dailyActivities: [
      'Inspect products against quality standards',
      'Document defects and quality issues',
      'Test product functionality and measurements',
      'Report findings to production team'
    ],
    requiredSkills: ['attention to detail', 'analytical thinking', 'technical understanding', 'documentation'],
    niceToHave: ['quality certifications', 'measurement tools', 'statistical analysis'],
    commonTransitionsFrom: ['manufacturing', 'warehouse', 'technical support', 'lab work']
  },
  {
    id: 'purchasing_coordinator',
    title: 'Purchasing Coordinator',
    category: 'Operations',
    keywords: ['operations', 'purchasing', 'organization', 'negotiation', 'admin', 'vendor'],
    salary: { entry_min: 38000, entry_max: 52000 },
    description: 'Coordinate purchasing activities, manage vendor relationships, and ensure timely procurement.',
    dailyActivities: [
      'Process purchase orders and requisitions',
      'Communicate with vendors on orders',
      'Track shipments and resolve issues',
      'Maintain purchasing records and contracts'
    ],
    requiredSkills: ['organization', 'communication', 'negotiation', 'data entry'],
    niceToHave: ['procurement software', 'vendor management', 'contract knowledge'],
    commonTransitionsFrom: ['admin', 'logistics', 'retail', 'customer service']
  },
  {
    id: 'production_scheduler',
    title: 'Production Scheduler',
    category: 'Operations',
    keywords: ['operations', 'scheduling', 'organization', 'manufacturing', 'planning', 'coordination'],
    salary: { entry_min: 42000, entry_max: 56000 },
    description: 'Plan and schedule production runs, coordinate resources, and ensure on-time delivery.',
    dailyActivities: [
      'Create production schedules based on orders',
      'Coordinate with production and materials teams',
      'Adjust schedules for changes and delays',
      'Track production progress and capacity'
    ],
    requiredSkills: ['organization', 'planning', 'problem-solving', 'manufacturing knowledge'],
    niceToHave: ['ERP systems', 'lean manufacturing', 'Excel proficiency'],
    commonTransitionsFrom: ['manufacturing', 'logistics', 'operations coordinator', 'warehouse']
  },
  {
    id: 'dispatch_coordinator',
    title: 'Dispatch Coordinator',
    category: 'Operations',
    keywords: ['operations', 'logistics', 'coordination', 'communication', 'scheduling', 'fast-paced'],
    salary: { entry_min: 36000, entry_max: 48000 },
    description: 'Coordinate drivers and vehicle dispatching, optimize routes, and manage delivery schedules.',
    dailyActivities: [
      'Assign drivers to routes and deliveries',
      'Monitor real-time delivery status',
      'Resolve issues and delays in transit',
      'Communicate with drivers and customers'
    ],
    requiredSkills: ['multitasking', 'communication', 'problem-solving', 'stress management'],
    niceToHave: ['dispatch software', 'routing knowledge', 'logistics experience'],
    commonTransitionsFrom: ['driver', 'logistics', 'customer service', 'warehouse']
  },

  // ===================================
  // TECHNICAL & IT SUPPORT (6 careers)
  // ===================================
  {
    id: 'it_help_desk',
    title: 'IT Help Desk Technician',
    category: 'IT Support',
    keywords: ['IT', 'technical_support', 'troubleshooting', 'customer_service', 'computers', 'tech'],
    salary: { entry_min: 38000, entry_max: 50000 },
    description: 'Provide first-level technical support to end users for hardware, software, and network issues.',
    dailyActivities: [
      'Troubleshoot desktop and software issues',
      'Set up new user accounts and equipment',
      'Document issues in ticketing system',
      'Escalate complex problems to senior IT staff'
    ],
    requiredSkills: ['technical troubleshooting', 'customer service', 'Windows/Mac', 'basic networking'],
    niceToHave: ['CompTIA A+', 'Active Directory', 'ticketing systems'],
    commonTransitionsFrom: ['technical support', 'retail tech', 'computer repair', 'customer service']
  },
  {
    id: 'network_technician',
    title: 'Network Technician',
    category: 'IT Support',
    keywords: ['IT', 'networking', 'technical', 'troubleshooting', 'infrastructure', 'tech'],
    salary: { entry_min: 42000, entry_max: 58000 },
    description: 'Install, maintain, and troubleshoot network infrastructure including routers, switches, and cabling.',
    dailyActivities: [
      'Install and configure network equipment',
      'Troubleshoot connectivity issues',
      'Perform network maintenance and updates',
      'Document network configurations'
    ],
    requiredSkills: ['networking knowledge', 'technical troubleshooting', 'cabling', 'problem-solving'],
    niceToHave: ['Network+', 'CCNA', 'specific vendor certifications'],
    commonTransitionsFrom: ['IT help desk', 'computer repair', 'telecom', 'technical support']
  },
  {
    id: 'data_entry_specialist',
    title: 'Data Entry Specialist',
    category: 'IT Support',
    keywords: ['data', 'detail', 'admin', 'typing', 'organized', 'computer'],
    salary: { entry_min: 30000, entry_max: 40000 },
    description: 'Input, update, and maintain accurate data in computer systems and databases.',
    dailyActivities: [
      'Enter data from various sources accurately',
      'Verify data integrity and accuracy',
      'Update and maintain databases',
      'Generate reports from data systems'
    ],
    requiredSkills: ['typing speed', 'attention to detail', 'data entry', 'computer skills'],
    niceToHave: ['specific software knowledge', 'Excel proficiency', '10-key'],
    commonTransitionsFrom: ['admin', 'receptionist', 'customer service', 'any entry-level']
  },
  {
    id: 'systems_administrator_junior',
    title: 'Junior Systems Administrator',
    category: 'IT Support',
    keywords: ['IT', 'systems', 'technical', 'administration', 'infrastructure', 'servers'],
    salary: { entry_min: 48000, entry_max: 65000 },
    description: 'Assist in managing servers, networks, and IT infrastructure under supervision of senior administrators.',
    dailyActivities: [
      'Monitor system performance and alerts',
      'Perform backups and system maintenance',
      'Assist with user account management',
      'Document system configurations and procedures'
    ],
    requiredSkills: ['IT fundamentals', 'troubleshooting', 'Windows/Linux', 'learning mindset'],
    niceToHave: ['Server administration', 'scripting', 'cloud platforms'],
    commonTransitionsFrom: ['IT help desk', 'network technician', 'technical support']
  },
  {
    id: 'av_technician',
    title: 'Audio/Visual Technician',
    category: 'IT Support',
    keywords: ['technical', 'av', 'hands-on', 'troubleshooting', 'setup', 'events'],
    salary: { entry_min: 38000, entry_max: 52000 },
    description: 'Set up, operate, and maintain audio/visual equipment for meetings, events, and presentations.',
    dailyActivities: [
      'Set up projectors, microphones, and speakers',
      'Troubleshoot AV equipment issues',
      'Provide tech support during events',
      'Maintain inventory of AV equipment'
    ],
    requiredSkills: ['technical setup', 'troubleshooting', 'customer service', 'hands-on'],
    niceToHave: ['CTS certification', 'specific equipment knowledge', 'event experience'],
    commonTransitionsFrom: ['technical support', 'events', 'IT help desk', 'entertainment']
  },
  {
    id: 'cybersecurity_analyst_junior',
    title: 'Junior Cybersecurity Analyst',
    category: 'IT Support',
    keywords: ['IT', 'security', 'analysis', 'technical', 'monitoring', 'cybersecurity'],
    salary: { entry_min: 50000, entry_max: 68000 },
    description: 'Monitor security systems, investigate alerts, and assist in protecting organizational assets.',
    dailyActivities: [
      'Monitor security alerts and logs',
      'Investigate potential security incidents',
      'Assist with vulnerability assessments',
      'Document security events and procedures'
    ],
    requiredSkills: ['IT fundamentals', 'analytical thinking', 'attention to detail', 'security awareness'],
    niceToHave: ['Security+', 'CISSP', 'networking knowledge'],
    commonTransitionsFrom: ['IT help desk', 'systems admin', 'network technician']
  },

  // ===================================
  // SALES & BUSINESS DEVELOPMENT (5 careers)
  // ===================================
  {
    id: 'sales_development_rep',
    title: 'Sales Development Representative',
    category: 'Sales',
    keywords: ['sales', 'communication', 'persuasion', 'outbound', 'business_development'],
    salary: { entry_min: 40000, entry_max: 60000 },
    description: 'Generate new business opportunities through outbound prospecting, qualifying leads, and setting appointments.',
    dailyActivities: [
      'Make outbound calls and emails to prospects',
      'Qualify leads and assess fit',
      'Schedule discovery calls for account executives',
      'Track activities and results in CRM'
    ],
    requiredSkills: ['communication', 'persistence', 'persuasion', 'goal-oriented'],
    niceToHave: ['sales experience', 'CRM tools', 'specific industry knowledge'],
    commonTransitionsFrom: ['customer service', 'retail', 'call center', 'any entry-level']
  },
  {
    id: 'account_executive',
    title: 'Account Executive',
    category: 'Sales',
    keywords: ['sales', 'business_development', 'negotiation', 'relationship', 'closing'],
    salary: { entry_min: 50000, entry_max: 80000 },
    description: 'Close new business deals, manage sales pipeline, and achieve revenue targets through consultative selling.',
    dailyActivities: [
      'Conduct discovery calls and product demos',
      'Create proposals and negotiate contracts',
      'Manage sales pipeline and forecast',
      'Build relationships with decision makers'
    ],
    requiredSkills: ['sales', 'negotiation', 'presentation', 'relationship building'],
    niceToHave: ['sales training', 'CRM expertise', 'industry knowledge'],
    commonTransitionsFrom: ['SDR', 'customer success', 'retail management', 'business development']
  },
  {
    id: 'inside_sales_rep',
    title: 'Inside Sales Representative',
    category: 'Sales',
    keywords: ['sales', 'communication', 'phone', 'customer_service', 'persuasion'],
    salary: { entry_min: 38000, entry_max: 55000 },
    description: 'Sell products or services remotely via phone, email, and video, managing the entire sales process.',
    dailyActivities: [
      'Follow up on inbound leads',
      'Conduct product presentations via phone/video',
      'Quote pricing and close deals',
      'Meet monthly/quarterly sales quotas'
    ],
    requiredSkills: ['sales', 'communication', 'persistence', 'CRM'],
    niceToHave: ['sales experience', 'product knowledge', 'tech savvy'],
    commonTransitionsFrom: ['customer service', 'retail', 'call center', 'SDR']
  },
  {
    id: 'retail_sales_associate',
    title: 'Retail Sales Associate',
    category: 'Sales',
    keywords: ['sales', 'retail', 'customer_service', 'people', 'product_knowledge'],
    salary: { entry_min: 28000, entry_max: 38000 },
    description: 'Assist customers in retail environment, provide product recommendations, and process transactions.',
    dailyActivities: [
      'Greet customers and assess needs',
      'Demonstrate products and features',
      'Process sales transactions and returns',
      'Maintain store appearance and inventory'
    ],
    requiredSkills: ['customer service', 'sales', 'communication', 'product knowledge'],
    niceToHave: ['POS systems', 'inventory management', 'specific product expertise'],
    commonTransitionsFrom: ['any entry-level', 'food service', 'hospitality']
  },
  {
    id: 'business_development_coordinator',
    title: 'Business Development Coordinator',
    category: 'Sales',
    keywords: ['business_development', 'sales', 'organization', 'research', 'coordination'],
    salary: { entry_min: 42000, entry_max: 56000 },
    description: 'Support business development team with research, outreach coordination, and sales operations.',
    dailyActivities: [
      'Research potential clients and markets',
      'Coordinate outreach campaigns',
      'Prepare proposals and presentations',
      'Track business development metrics'
    ],
    requiredSkills: ['research', 'organization', 'communication', 'business acumen'],
    niceToHave: ['sales experience', 'CRM tools', 'market research'],
    commonTransitionsFrom: ['admin', 'customer service', 'marketing coordinator', 'sales support']
  },

  // ===================================
  // ADMINISTRATIVE & COORDINATION (7 careers)
  // ===================================
  {
    id: 'administrative_assistant',
    title: 'Administrative Assistant',
    category: 'Administrative',
    keywords: ['admin', 'organization', 'office', 'coordination', 'support', 'clerical'],
    salary: { entry_min: 32000, entry_max: 45000 },
    description: 'Provide administrative support including scheduling, correspondence, and office management.',
    dailyActivities: [
      'Manage calendars and schedule meetings',
      'Handle correspondence and phone calls',
      'Prepare documents and presentations',
      'Coordinate travel arrangements'
    ],
    requiredSkills: ['organization', 'Microsoft Office', 'communication', 'multitasking'],
    niceToHave: ['executive support', 'industry-specific knowledge', 'advanced Office skills'],
    commonTransitionsFrom: ['receptionist', 'customer service', 'retail', 'any entry-level']
  },
  {
    id: 'executive_assistant',
    title: 'Executive Assistant',
    category: 'Administrative',
    keywords: ['admin', 'executive', 'organization', 'coordination', 'high-level', 'support'],
    salary: { entry_min: 48000, entry_max: 68000 },
    description: 'Provide high-level administrative support to executives, managing complex schedules and projects.',
    dailyActivities: [
      'Manage executive calendars and priorities',
      'Coordinate high-stakes meetings and travel',
      'Handle confidential information',
      'Prepare executive communications and reports'
    ],
    requiredSkills: ['organization', 'discretion', 'anticipation', 'advanced Office skills'],
    niceToHave: ['previous executive support', 'project management', 'industry knowledge'],
    commonTransitionsFrom: ['administrative assistant', 'office manager', 'project coordinator']
  },
  {
    id: 'office_manager',
    title: 'Office Manager',
    category: 'Administrative',
    keywords: ['admin', 'management', 'organization', 'operations', 'facilities', 'coordination'],
    salary: { entry_min: 42000, entry_max: 58000 },
    description: 'Oversee office operations, manage vendors, coordinate facilities, and ensure smooth daily functioning.',
    dailyActivities: [
      'Manage office supplies and vendors',
      'Coordinate facilities and maintenance',
      'Handle HR administrative tasks',
      'Manage office budget and expenses'
    ],
    requiredSkills: ['organization', 'management', 'vendor relations', 'problem-solving'],
    niceToHave: ['HR knowledge', 'budget management', 'facilities experience'],
    commonTransitionsFrom: ['administrative assistant', 'operations coordinator', 'retail management']
  },
  {
    id: 'receptionist',
    title: 'Receptionist',
    category: 'Administrative',
    keywords: ['admin', 'customer_service', 'communication', 'office', 'phones', 'front_desk'],
    salary: { entry_min: 28000, entry_max: 38000 },
    description: 'Greet visitors, answer phones, and provide front desk support to maintain professional office environment.',
    dailyActivities: [
      'Greet visitors and manage front desk',
      'Answer and route phone calls',
      'Schedule appointments and meetings',
      'Handle mail and deliveries'
    ],
    requiredSkills: ['communication', 'customer service', 'multitasking', 'professional demeanor'],
    niceToHave: ['phone systems', 'scheduling software', 'office experience'],
    commonTransitionsFrom: ['customer service', 'retail', 'hospitality', 'any entry-level']
  },
  {
    id: 'project_coordinator',
    title: 'Project Coordinator',
    category: 'Administrative',
    keywords: ['project_management', 'coordination', 'organization', 'admin', 'tracking'],
    salary: { entry_min: 42000, entry_max: 56000 },
    description: 'Support project managers by coordinating tasks, tracking timelines, and facilitating team communication.',
    dailyActivities: [
      'Track project milestones and deadlines',
      'Coordinate meetings and communications',
      'Maintain project documentation',
      'Update project management tools'
    ],
    requiredSkills: ['organization', 'coordination', 'communication', 'attention to detail'],
    niceToHave: ['project management tools', 'Agile/Scrum', 'specific industry knowledge'],
    commonTransitionsFrom: ['admin assistant', 'operations coordinator', 'customer service']
  },
  {
    id: 'scheduler',
    title: 'Scheduler',
    category: 'Administrative',
    keywords: ['scheduling', 'organization', 'coordination', 'admin', 'calendar', 'planning'],
    salary: { entry_min: 35000, entry_max: 47000 },
    description: 'Manage complex scheduling for teams, resources, or facilities, optimizing utilization and preventing conflicts.',
    dailyActivities: [
      'Manage multiple calendars and schedules',
      'Coordinate resource allocation',
      'Resolve scheduling conflicts',
      'Track and report utilization metrics'
    ],
    requiredSkills: ['organization', 'attention to detail', 'communication', 'problem-solving'],
    niceToHave: ['scheduling software', 'industry-specific knowledge', 'resource planning'],
    commonTransitionsFrom: ['admin', 'receptionist', 'customer service', 'coordinator roles']
  },
  {
    id: 'hr_coordinator',
    title: 'HR Coordinator',
    category: 'Administrative',
    keywords: ['hr', 'admin', 'organization', 'people', 'coordination', 'support'],
    salary: { entry_min: 38000, entry_max: 50000 },
    description: 'Support human resources functions including recruiting, onboarding, benefits, and employee relations.',
    dailyActivities: [
      'Coordinate new hire onboarding',
      'Maintain employee records and files',
      'Assist with benefits administration',
      'Schedule interviews and training sessions'
    ],
    requiredSkills: ['organization', 'confidentiality', 'communication', 'attention to detail'],
    niceToHave: ['HRIS systems', 'HR certification', 'employment law basics'],
    commonTransitionsFrom: ['admin assistant', 'receptionist', 'customer service', 'office manager']
  },

  // ===================================
  // TRADES & SKILLED LABOR (5 careers)
  // ===================================
  {
    id: 'hvac_technician',
    title: 'HVAC Technician',
    category: 'Trades',
    keywords: ['trades', 'technical', 'hands-on', 'hvac', 'maintenance', 'repair'],
    salary: { entry_min: 40000, entry_max: 60000 },
    description: 'Install, maintain, and repair heating, ventilation, and air conditioning systems.',
    dailyActivities: [
      'Diagnose HVAC system problems',
      'Perform installations and repairs',
      'Conduct maintenance and inspections',
      'Test system performance and efficiency'
    ],
    requiredSkills: ['technical knowledge', 'hands-on skills', 'problem-solving', 'customer service'],
    niceToHave: ['EPA certification', 'trade license', 'specific system expertise'],
    commonTransitionsFrom: ['maintenance', 'construction', 'manufacturing', 'technical support']
  },
  {
    id: 'electrical_apprentice',
    title: 'Electrical Apprentice',
    category: 'Trades',
    keywords: ['trades', 'electrical', 'hands-on', 'technical', 'construction', 'apprentice'],
    salary: { entry_min: 35000, entry_max: 50000 },
    description: 'Learn electrical trade through on-the-job training while assisting licensed electricians.',
    dailyActivities: [
      'Assist with electrical installations',
      'Learn wiring and circuit systems',
      'Read and interpret blueprints',
      'Follow safety protocols strictly'
    ],
    requiredSkills: ['hands-on learning', 'technical aptitude', 'safety awareness', 'physical stamina'],
    niceToHave: ['basic electrical knowledge', 'construction experience', 'trade school'],
    commonTransitionsFrom: ['construction', 'maintenance', 'manufacturing', 'any entry-level']
  },
  {
    id: 'plumbing_apprentice',
    title: 'Plumbing Apprentice',
    category: 'Trades',
    keywords: ['trades', 'plumbing', 'hands-on', 'technical', 'maintenance', 'apprentice'],
    salary: { entry_min: 32000, entry_max: 46000 },
    description: 'Learn plumbing trade while assisting licensed plumbers with installations and repairs.',
    dailyActivities: [
      'Assist with pipe installations and repairs',
      'Learn plumbing codes and standards',
      'Use various plumbing tools and equipment',
      'Perform basic maintenance tasks'
    ],
    requiredSkills: ['hands-on learning', 'physical stamina', 'problem-solving', 'customer service'],
    niceToHave: ['basic plumbing knowledge', 'construction experience', 'trade school'],
    commonTransitionsFrom: ['construction', 'maintenance', 'warehouse', 'any entry-level']
  },
  {
    id: 'automotive_technician',
    title: 'Automotive Technician',
    category: 'Trades',
    keywords: ['trades', 'automotive', 'technical', 'hands-on', 'repair', 'mechanical'],
    salary: { entry_min: 38000, entry_max: 55000 },
    description: 'Diagnose, maintain, and repair vehicles including engines, electrical systems, and mechanical components.',
    dailyActivities: [
      'Perform vehicle inspections and diagnostics',
      'Repair and replace automotive parts',
      'Conduct routine maintenance services',
      'Test drive vehicles after repairs'
    ],
    requiredSkills: ['mechanical knowledge', 'diagnostic skills', 'hands-on repair', 'attention to detail'],
    niceToHave: ['ASE certification', 'specific brand training', 'diagnostic tools'],
    commonTransitionsFrom: ['mechanic helper', 'maintenance', 'manufacturing', 'technical support']
  },
  {
    id: 'facilities_maintenance',
    title: 'Facilities Maintenance Technician',
    category: 'Trades',
    keywords: ['maintenance', 'hands-on', 'technical', 'repair', 'facilities', 'building'],
    salary: { entry_min: 36000, entry_max: 50000 },
    description: 'Maintain and repair building systems, equipment, and facilities to ensure proper functionality.',
    dailyActivities: [
      'Perform routine maintenance on buildings',
      'Repair plumbing, electrical, and HVAC issues',
      'Respond to maintenance requests',
      'Inspect facilities and equipment regularly'
    ],
    requiredSkills: ['general maintenance', 'hands-on repair', 'troubleshooting', 'safety awareness'],
    niceToHave: ['specific trade certifications', 'HVAC/electrical/plumbing knowledge', 'facilities experience'],
    commonTransitionsFrom: ['maintenance', 'construction', 'warehouse', 'manufacturing']
  },

  // ===================================
  // CREATIVE & MEDIA (4 careers)
  // ===================================
  {
    id: 'graphic_designer_junior',
    title: 'Junior Graphic Designer',
    category: 'Creative',
    keywords: ['creative', 'design', 'visual', 'marketing', 'adobe', 'artistic'],
    salary: { entry_min: 38000, entry_max: 52000 },
    description: 'Create visual content including graphics, layouts, and designs for print and digital media.',
    dailyActivities: [
      'Design graphics for marketing materials',
      'Create social media graphics',
      'Collaborate with marketing team on concepts',
      'Revise designs based on feedback'
    ],
    requiredSkills: ['Adobe Creative Suite', 'design principles', 'creativity', 'attention to detail'],
    niceToHave: ['portfolio', 'specific industry experience', 'web design'],
    commonTransitionsFrom: ['freelance design', 'social media', 'marketing assistant', 'retail']
  },
  {
    id: 'video_editor',
    title: 'Video Editor',
    category: 'Creative',
    keywords: ['creative', 'video', 'editing', 'media', 'production', 'technical'],
    salary: { entry_min: 40000, entry_max: 58000 },
    description: 'Edit video content for various platforms, adding effects, audio, and graphics to create polished final products.',
    dailyActivities: [
      'Edit raw footage into cohesive videos',
      'Add music, sound effects, and graphics',
      'Color correct and enhance video quality',
      'Export videos in various formats'
    ],
    requiredSkills: ['video editing software', 'storytelling', 'attention to detail', 'creativity'],
    niceToHave: ['specific software expertise', 'motion graphics', 'portfolio'],
    commonTransitionsFrom: ['production assistant', 'freelance editing', 'social media', 'photography']
  },
  {
    id: 'social_media_coordinator',
    title: 'Social Media Coordinator',
    category: 'Creative',
    keywords: ['social_media', 'marketing', 'content', 'creative', 'communication', 'digital'],
    salary: { entry_min: 36000, entry_max: 50000 },
    description: 'Manage social media accounts, create content, and engage with online communities to build brand presence.',
    dailyActivities: [
      'Create and schedule social media posts',
      'Engage with followers and respond to comments',
      'Track social media metrics and performance',
      'Collaborate on content strategy'
    ],
    requiredSkills: ['social media platforms', 'content creation', 'communication', 'creativity'],
    niceToHave: ['scheduling tools', 'analytics', 'graphic design basics'],
    commonTransitionsFrom: ['customer service', 'marketing assistant', 'retail', 'freelance content']
  },
  {
    id: 'content_writer',
    title: 'Content Writer',
    category: 'Creative',
    keywords: ['writing', 'content', 'marketing', 'communication', 'creative', 'copywriting'],
    salary: { entry_min: 38000, entry_max: 54000 },
    description: 'Write engaging content for websites, blogs, social media, and marketing materials.',
    dailyActivities: [
      'Research and write blog posts and articles',
      'Create website copy and product descriptions',
      'Write social media captions',
      'Edit and proofread content'
    ],
    requiredSkills: ['writing', 'research', 'SEO basics', 'grammar'],
    niceToHave: ['portfolio', 'specific niche expertise', 'CMS experience'],
    commonTransitionsFrom: ['any writing background', 'customer service', 'admin', 'social media']
  }
];

export default EXPANDED_CAREERS;

// Legacy export for backward compatibility
export const expandedCareers = EXPANDED_CAREERS;
